// apps/nos/client/src/lib/monitoring.ts
// Same API surface as the server's logger. Use everywhere instead of console.error/warn.

import { Sentry, sentryEnabled } from "./sentryInit";

export type LogOptions = {
  fingerprint?: string[];
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
};

type Categorized =
  | { kind: "error"; err: Error }
  | { kind: "options"; opts: LogOptions }
  | { kind: "extras"; obj: Record<string, unknown> }
  | { kind: "string"; val: string }
  | { kind: "none" };

function categorize(arg: unknown): Categorized {
  if (arg === undefined || arg === null) return { kind: "none" };
  if (arg instanceof Error) return { kind: "error", err: arg };
  if (typeof arg === "string") return { kind: "string", val: arg };
  if (typeof arg === "object") {
    const o = arg as Record<string, unknown>;
    if ("fingerprint" in o || "tags" in o || "extra" in o || "level" in o) {
      return { kind: "options", opts: o as LogOptions };
    }
    return { kind: "extras", obj: o };
  }
  return { kind: "extras", obj: { value: arg } };
}

function parseArgs(second: unknown, third: unknown) {
  const a = categorize(second);
  const b = categorize(third);
  const out: { err?: Error; extras?: Record<string, unknown>; opts?: LogOptions; legacyTail?: string } = {};
  if (a.kind === "error") out.err = a.err;
  else if (a.kind === "extras") out.extras = a.obj;
  else if (a.kind === "options") out.opts = a.opts;
  else if (a.kind === "string") out.legacyTail = a.val;
  if (b.kind === "options") out.opts = b.opts;
  else if (b.kind === "extras" && !out.extras) out.extras = b.obj;
  else if (b.kind === "error" && !out.err) out.err = b.err;
  return out;
}

function applyScope(scope: Sentry.Scope, parsed: ReturnType<typeof parseArgs>, eventKey: string): void {
  if (parsed.extras) for (const [k, v] of Object.entries(parsed.extras)) scope.setExtra(k, v);
  if (parsed.opts?.extra) for (const [k, v] of Object.entries(parsed.opts.extra)) scope.setExtra(k, v);
  if (parsed.opts?.tags) for (const [k, v] of Object.entries(parsed.opts.tags)) scope.setTag(k, v);
  scope.setFingerprint(parsed.opts?.fingerprint ?? [eventKey]);
}

export const monitoring = {
  info(eventKey: string, details?: unknown, options?: unknown): void {
    const parsed = parseArgs(details, options);
    console.log(`[INFO] ${eventKey}`, parsed.extras ?? parsed.err ?? "");
    if (!sentryEnabled) return;
    const data = parsed.extras;
    Sentry.addBreadcrumb({
      category: "log",
      level: "info",
      message: eventKey,
      ...(data ? { data } : {}),
    });
  },

  warn(eventKey: string, details?: unknown, options?: unknown): void {
    const parsed = parseArgs(details, options);
    console.warn(`[WARN] ${eventKey}`, parsed.extras ?? parsed.err ?? "");
    if (!sentryEnabled) return;
    Sentry.withScope((scope) => {
      scope.setLevel(parsed.opts?.level ?? "warning");
      applyScope(scope, parsed, eventKey);
      if (parsed.err) Sentry.captureException(parsed.err);
      else Sentry.captureMessage(eventKey);
    });
  },

  error(eventKey: string, details?: unknown, options?: unknown): void {
    const parsed = parseArgs(details, options);
    console.error(`[ERROR] ${eventKey}`, parsed.extras ?? parsed.err ?? "");
    if (!sentryEnabled) return;
    Sentry.withScope((scope) => {
      scope.setLevel(parsed.opts?.level ?? "error");
      applyScope(scope, parsed, eventKey);
      if (parsed.err) Sentry.captureException(parsed.err);
      else Sentry.captureMessage(eventKey, "error");
    });
  },

  debug(eventKey: string, details?: unknown): void {
    console.debug(`[DEBUG] ${eventKey}`, details ?? "");
  },
};

export default monitoring;
